import {notifications} from '@mantine/notifications';
import {useCallback, useRef} from 'react';
import {SELECTION_PHASE_IDS} from '../../../core/entities/reportAnalytics/schema';
import {useAiGenerateEntryTodos} from '../../../core/repositories/entryTodosRepository';
import {showErrorSnackBar} from '../../../shared/components';
import type {TodoCandidateType} from './useAiTodoGeneration';

const NOTIFICATION_ID = 'ai-todo-generation-progress';

type UseAiTodoGenerationFlowArgs = {
  onGenerationSuccess: (todos: TodoCandidateType[]) => void;
  onGenerationError: (wasBackground: boolean) => void;
  resetAllStates: () => void;
};

export const useAiTodoGenerationFlow = ({
  onGenerationSuccess,
  onGenerationError,
  resetAllStates,
}: UseAiTodoGenerationFlowArgs) => {
  const isGeneratingInBackgroundRef = useRef(false);
  const hasAutoGeneratedRef = useRef(false);

  const aiGenerateMutation = useAiGenerateEntryTodos();

  const handleStepSubmit = useCallback(
    async (
      stepId: number,
      selectedDepartment: string,
      overrideStepId?: number,
    ) => {
      const actualStepId = overrideStepId ?? stepId;
      if (!actualStepId) return;

      const [deptIdStr, reportType] = selectedDepartment.split('-');
      const departmentID = Number(deptIdStr);
      const selectionPhaseID =
        reportType === 'intern'
          ? SELECTION_PHASE_IDS.INTERN
          : SELECTION_PHASE_IDS.SELECTION;

      try {
        const result = await aiGenerateMutation.mutateAsync({
          stepID: actualStepId,
          departmentID,
          selectionPhaseID,
        });

        // バックグラウンド生成完了時は通知を非表示
        const wasBackground = isGeneratingInBackgroundRef.current;
        if (wasBackground) {
          notifications.hide(NOTIFICATION_ID);
          isGeneratingInBackgroundRef.current = false;
        }

        if (result.ok && result.value.todos.length > 0) {
          onGenerationSuccess(result.value.todos);
        } else {
          showErrorSnackBar({
            title: 'AI ToDo生成',
            message: 'ToDoの生成に失敗しました',
          });
          onGenerationError(wasBackground);
        }
      } catch (_error) {
        // バックグラウンド生成完了時は通知を非表示
        if (isGeneratingInBackgroundRef.current) {
          notifications.hide(NOTIFICATION_ID);
          isGeneratingInBackgroundRef.current = false;
          showErrorSnackBar({
            title: 'AI ToDo生成',
            message: 'ToDoの生成に失敗しました',
          });
          resetAllStates();
        } else {
          showErrorSnackBar({
            title: 'AI ToDo生成',
            message: 'ToDoの生成に失敗しました',
          });
          onGenerationError(false);
        }
      }
    },
    [
      aiGenerateMutation,
      onGenerationSuccess,
      onGenerationError,
      resetAllStates,
    ],
  );

  const startBackgroundGeneration = useCallback(() => {
    isGeneratingInBackgroundRef.current = true;
    notifications.show({
      id: NOTIFICATION_ID,
      title: 'AI ToDo生成中',
      message:
        '選考予定に関連するToDoを自動生成しています... 結果を表示するにはページに留まってください',
      color: 'blue',
      loading: true,
      autoClose: false,
    });
  }, []);

  const resetGenerationFlow = useCallback(() => {
    hasAutoGeneratedRef.current = false;
  }, []);

  return {
    isGeneratingInBackgroundRef,
    hasAutoGeneratedRef,
    handleStepSubmit,
    startBackgroundGeneration,
    resetGenerationFlow,
  };
};
